/* A destination that exists in the product and has no screen yet — STORY-039.
 *
 * Listed rather than hidden, and this is the screen that makes that honest.
 * `design.md`: keep the unbuilt destinations visible, so the scope boundary is
 * stated where it is checked rather than only in a document. Hiding navigation
 * is how people conclude a feature does not exist.
 *
 * Each of these is deleted by the story that builds its destination.
 */

import type { MessageKey } from '@eventuley/i18n';
import { useTranslate } from '../shared/i18n/index.js';
import { Callout } from '../shared/ui/index.js';

export type Destination =
  | 'calendar'
  | 'events'
  | 'venues'
  | 'people'
  | 'tasks'
  | 'money'
  | 'inventory'
  | 'settings';

/* An explicit map rather than `t('nav.' + destination)`. `i18n.md` forbids a
 * constructed key: it defeats extraction and unused-key detection, both of
 * which are what keep the catalogue honest. */
const NAME_OF: Record<Destination, MessageKey> = {
  calendar: 'nav.calendar',
  events: 'nav.events',
  venues: 'nav.venues',
  people: 'nav.people',
  tasks: 'nav.tasks',
  money: 'nav.money',
  inventory: 'nav.inventory',
  settings: 'nav.settings',
};

export function NotBuiltScreen({ destination }: { readonly destination: Destination }) {
  const t = useTranslate();
  const name = t(NAME_OF[destination]);

  return (
    <>
      {/* The callout sits AFTER the head, not inside it. `page-head` is a
          space-between row for a title and its action, so a callout placed in
          it was pushed to the far right and baseline-aligned with the
          heading — leaving the two floating apart with the width of the
          content column between them, on every destination that is not built
          yet. */}
      <div className="page-head">
        <h1>{name}</h1>
      </div>
      {/* Two elements, never one string joined by a dash: a sentence assembled
          from translated fragments in English word order cannot be reassembled
          in Turkish, where suffixes attach to the word — `i18n.md`. */}
      <Callout tone="info">
        <strong>{t('nav.not_built_title', { destination: name })}</strong>
        <p>{t('nav.not_built_body')}</p>
      </Callout>
    </>
  );
}
